import { useCallback, useEffect, useState } from 'react';

import { fetchPeople } from '../api.js';
import type { Person } from '../api.js';

/**
 * `ready` with just the caller themself IS the "nobody shares with you yet"
 * state — shaped exactly like `useOrbit`/`useNodes`, so there is nothing more
 * to say about it than the list itself already shows.
 */
export type PeopleState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; people: Person[] };

export interface UsePeople {
  state: PeopleState;
  /**
   * Re-reads the people list. Needed because accepting an incoming invite is
   * what makes a new person appear here — the list cannot work that out for
   * itself — shaped exactly like `useOrbit`'s `{ state, reload }` so a caller
   * reloads both the same way.
   */
  reload: () => Promise<void>;
}

/** Loaded once per Dashboard mount: the caller first, then anyone who shares
 *  their own dashboard with the caller — the set the person selector switches
 *  between. */
export function usePeople(): UsePeople {
  const [state, setState] = useState<PeopleState>({ kind: 'loading' });

  const load = useCallback(async () => {
    try {
      const people = await fetchPeople();
      setState({ kind: 'ready', people });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Could not load the people sharing with you.',
      });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { state, reload: load };
}
